import { useEffect } from 'react';
import { Button, Group, Stack, Table, Text, TextInput } from '@mantine/core';
import {
  ChipRail,
  ConsoleTable,
  EstatePill,
  PageHeader,
  Pill,
  Row,
  TableMessage,
} from '../ui';
import { clockMs, latency, tokens as fmtTokens } from '../ui/format';
import { tokens as T, statusTone } from '../theme/tokens';
import { useEstatesStore, useLedgerStore } from '../stores/dataStores';
import { isFirstLoad, isUnconfigured, rowsOf } from '../stores/collection';
import { useUIStore, ledgerFilterLabels, type LedgerFilter } from '../stores/uiStore';
import { rowTokens, splitTenantKey, rowKind } from '../data/types';
import { LedgerDrawer } from '../features/ledger/LedgerDrawer';

const COLS = [
  { key: 'at', label: 'Occurred', w: 130 },
  { key: 'tenant', label: 'Tenant', w: 170 },
  { key: 'event', label: 'Event id' },
  { key: 'lane', label: 'Lane', w: 110 },
  { key: 'model', label: 'Model', w: 150 },
  { key: 'status', label: 'Status', w: 100 },
  { key: 'latency', label: 'Latency', w: 90 },
  { key: 'tokens', label: 'Tokens', w: 100 },
  { key: 'emitted', label: 'Emitted', w: 100 },
];

const FILTERS = (Object.keys(ledgerFilterLabels) as LedgerFilter[]).map((k) => ({
  value: k,
  label: ledgerFilterLabels[k],
}));

const PAGE_SIZES = [
  { value: '10', label: '10' },
  { value: '25', label: '25' },
  { value: '50', label: '50' },
];

export function LedgerPage() {
  const ui = useUIStore();
  const ledger = useLedgerStore((s) => s.data);
  const estates = rowsOf(useEstatesStore((s) => s.data));
  const estateOptions = estates.map((e) => ({
    value: e.id,
    // "hal — hal — local CLI" is the failure this guard exists for: some display
    // names already lead with the estate id.
    label: e.displayName.startsWith(e.id) ? e.displayName : `${e.id} — ${e.displayName}`,
  }));
  const open = useLedgerStore((s) => s.open);
  const close = useLedgerStore((s) => s.close);
  const scoped = ui.scope ? splitTenantKey(ui.scope) : null;

  useEffect(() => {
    open({
      ...(scoped ? { estateId: scoped.estateId, tenantId: scoped.tenantId } : {}),
      ...(ui.ledgerEstate !== 'all' && !scoped ? { estateId: ui.ledgerEstate } : {}),
      search: ui.ledgerSearch,
      filter: ui.ledgerFilter,
      limit: ui.ledgerPageSize,
      // Opaque, and always one the SOURCE handed back — never one this page
      // constructed. An offset synthesised here would work against the fixture
      // and break against the ledger's RFC3339Nano|requestId cursor.
      cursor: ui.ledgerPage > 0 ? (ui.ledgerCursors[ui.ledgerPage - 1] ?? '') : '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ui.scope, ui.ledgerEstate, ui.ledgerSearch, ui.ledgerFilter, ui.ledgerPageSize, ui.ledgerPage]);

  const all = rowsOf(ledger);
  // `conflicts` is the one filter Firestore cannot serve: conflictOf is
  // omitempty, so it is ABSENT on almost every document, and Firestore's only
  // "field exists" predicate is an inequality that would force the ordering
  // onto the digest instead of onto time. So it narrows the loaded window here
  // — correct order on a bounded set, rather than wrong order on an unbounded
  // one. Every other filter is a real server-side predicate.
  const rows =
    ui.ledgerFilter === 'conflicts' ? all.filter((r) => Boolean(r.conflictOf)) : all;
  const dirty =
    ui.ledgerSearch !== '' || ui.ledgerEstate !== 'all' || ui.ledgerFilter !== 'all';

  return (
    <Stack gap={12}>
      <PageHeader
        title="Usage ledger"
        lede={
          <>
            One durable row per served completion — billable and attributed — written before the
            terminal frame. This is what answers "what actually happened" when the collector drops
            telemetry. Served back estate-scoped over{' '}
            <Text component="span" ff="monospace">
              GET /v1/usage?from=&amp;to=&amp;cursor=
            </Text>
            .
          </>
        }
        actions={
          <>
            <Button
              variant="default"
              onClick={() =>
                ui.openModal({
                  kind: 'unavailable',
                  action: 'Exporting a ledger range',
                  detail:
                    'needs a control-plane route that can read across estates. GET /v1/usage derives the estate from the credential and takes no estate parameter, which is what makes cross-estate isolation structural rather than a check — so the operator export is a separate route, and it is not built.',
                })
              }
            >
              Export range
            </Button>
            <Button
              onClick={() =>
                ui.openModal({
                  kind: 'unavailable',
                  action: 'Replaying rows to the collector',
                  detail:
                    'needs a control-plane route that re-emits a ledger row as a usage record. The ledger exists and is the recovery source by design; the re-emit path on top of it is not built.',
                })
              }
            >
              Replay to collector
            </Button>
          </>
        }
      />

      <Group gap={10} wrap="wrap" mt={10}>
        <TextInput
          value={ui.ledgerSearch}
          onChange={(e) => ui.setLedgerQuery('ledgerSearch', e.currentTarget.value)}
          placeholder="Search event id, tenant, model, status…"
          leftSection={<Text c={T.muted}>⌕</Text>}
          style={{ flex: '1 1 280px', minWidth: 230, maxWidth: 440 }}
        />
        {ui.scope ? null : (
          <ChipRail
            value={ui.ledgerEstate}
            onChange={(v) => ui.setLedgerQuery('ledgerEstate', v)}
            data={[
              { value: 'all', label: 'All estates' },
              ...estateOptions,
            ]}
          />
        )}
        <ChipRail
          value={ui.ledgerFilter}
          onChange={(v) => ui.setLedgerQuery('ledgerFilter', v)}
          data={FILTERS}
        />
      </Group>

      <Group gap={12}>
        <Text fz={12} c={T.ink4}>
          {rows.length === 0
            ? 'No rows match'
            : `Showing ${ui.ledgerPage * ui.ledgerPageSize + 1}–${
                ui.ledgerPage * ui.ledgerPageSize + rows.length
              }`}
        </Text>
        {dirty ? (
          <Text
            fz={12}
            fw={700}
            c={T.coralDeep}
            style={{ cursor: 'pointer' }}
            onClick={() => ui.clearLedgerFilters()}
          >
            Clear filters
          </Text>
        ) : null}
      </Group>

      <ConsoleTable cols={COLS} minWidth={1240} hover>
        {rows.map((r) => (
          <Row
            key={r.id}
            selected={ui.ledgerSelection === r.id}
            onClick={() => ui.selectLedgerRow(r.id)}
          >
            <Table.Td>
              <Text fz={12} c={T.ink3}>
                {clockMs(r.occurredAt)}
              </Text>
            </Table.Td>
            <Table.Td>
              <Group gap={7} wrap="nowrap" style={{ minWidth: 0 }}>
                <EstatePill estate={r.estateId} />
                <Text fz={12} c={T.ink2} truncate>
                  {r.tenantId}
                </Text>
              </Group>
            </Table.Td>
            <Table.Td>
              <Text ff="monospace" fz={12} c={r.billable ? T.ink2 : T.ink5} truncate>
                {r.billable ? r.eventId : '— attributed, no envelope'}
              </Text>
            </Table.Td>
            <Table.Td>
              <Text fz={12} fw={700} c={r.billable ? T.ink2 : T.ink5}>
                {r.billable ? r.lane : 'none'}
              </Text>
            </Table.Td>
            <Table.Td>
              <Group gap={6} wrap="nowrap">
                {rowKind(r) !== 'completion' ? (
                  <Pill tone="neutral" size="sm">
                    {rowKind(r) === 'memory'
                      ? `memory · ${r.memory?.op}`
                      : rowKind(r) === 'compact'
                        ? `compact · ${r.compact?.folds ?? 0} fold${r.compact?.folds === 1 ? '' : 's'}`
                        : 'search'}
                  </Pill>
                ) : null}
                <Text fz={12} c={T.ink2} truncate>
                  {r.requestModel}
                </Text>
              </Group>
            </Table.Td>
            <Table.Td>
              <Pill
                tone={r.conflictOf ? 'fail' : r.refused ? 'warn' : statusTone(r.status)}
                size="md"
              >
                {r.conflictOf ? 'conflict' : r.refused ? 'refused' : r.status}
              </Pill>
            </Table.Td>
            <Table.Td>
              <Text fz={12} c={T.ink2}>
                {latency(r.latencyMs)}
              </Text>
            </Table.Td>
            <Table.Td>
              <Text fz={12} c={T.ink2}>
                {fmtTokens(rowTokens(r))}
              </Text>
            </Table.Td>
            <Table.Td>
              <Text fz={12} fw={700} c={T.ink5} title={EMITTED_NOTE}>
                —
              </Text>
            </Table.Td>
          </Row>
        ))}
        {rows.length === 0 ? (
          <TableMessage
            span={COLS.length}
            title={
              isUnconfigured(ledger)
                ? 'Not connected — the ledger has no browser-readable route yet'
                : isFirstLoad(ledger)
                  ? 'Loading…'
                  : 'No rows match these filters'
            }
            detail={
              isUnconfigured(ledger)
                ? 'GET /v1/usage derives the estate from the credential, so an operator view spanning estates needs a control-plane route that does not exist yet.'
                : 'Widen the range, clear the estate filter, or search by a different event id.'
            }
          />
        ) : null}
      </ConsoleTable>

      <Group justify="space-between" gap={16} wrap="wrap" mt={2}>
        <Group gap={10}>
          <Text fz={12} c={T.ink4}>
            Rows per page
          </Text>
          <ChipRail
            size="sm"
            value={String(ui.ledgerPageSize)}
            onChange={(v) => ui.setLedgerQuery('ledgerPageSize', Number(v))}
            data={PAGE_SIZES}
          />
        </Group>
        <Group gap={12}>
          <Text fz={12} c={T.ink4}>
            Page {ui.ledgerPage + 1}
          </Text>
          <Button variant="default" disabled={ui.ledgerPage === 0} onClick={() => ui.prevLedgerPage()}>
            ← Previous
          </Button>
          <Button
            variant="default"
            disabled={!ledger.nextCursor}
            onClick={() => ledger.nextCursor && ui.nextLedgerPage(ledger.nextCursor)}
          >
            Next cursor →
          </Button>
        </Group>
      </Group>

      <Text fz={12} c={T.ink4} lh={1.6} maw={880}>
        Dedup identity is{' '}
        <Text component="span" ff="monospace">
          (estate, tenant, event id)
        </Text>
        ; the document id is SHA-256/128 over the length-prefixed tuple and every read compares the
        stored tuple back. A digest match with a tuple mismatch is an incident, never a dedup —
        those rows carry{' '}
        <Text component="span" ff="monospace">
          conflictOf
        </Text>{' '}
        and are flagged above.
      </Text>

      <LedgerDrawer />
    </Stack>
  );
}

/** DR-16 removed UsageRow.Emitted as optimistic evidence. The column is in the
 *  design and is built, but nothing populates it — see spec/decisions.md DR-28. */
export const EMITTED_NOTE =
  'No backing field: UsageRow.Emitted was removed by DR-16. Delivery gaps are found by the reconciliation join, not by a flag.';
